import React, { useState, useEffect } from'react';
import { useNavigate, useLocation } from'react-router-dom';
import { getApiUrl } from'../../services/api';
import { useAtozasTranslate } from'../../context/AtozasLanguageContext';

import LanguageSelector from'../common/LanguageSelector';
import ConfirmationModal from'../common/ConfirmationModal';

const Navbar = ({ onBecomeDriver, onDriverDashboard, onLogout, onHome, onProfile, onRides, user, isDriver = false, isOnDriverDashboard = false, onDriverTabChange, activeDriverTab ='dashboard', onNotificationClick, unreadCount = 0, onMenuClick }) => {
 const navigate = useNavigate();
 const location = useLocation();
 const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
 /** Embedded WebViews often break`position: sticky`; keep the bar viewport-fixed there at every width. */
 const [isWebViewEnv, setIsWebViewEnv] = useState(false);
 const hasNotifications = unreadCount > 0;

 useEffect(() => {
 if (typeof window ==='undefined') return;
 const ua = navigator.userAgent ||'';
 const isAndroidWebView = /\bwv\b|; wv\)/i.test(ua);
 const isIosWebView =
 /iPad|iPhone|iPod/i.test(ua) && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua);
 const isEmbeddedAppWebView = Boolean(
 window.ReactNativeWebView || window.flutter_inappwebview || window.webkit?.messageHandlers
 );
 const previewParams = new URLSearchParams(window.location.search);
 const forceWebViewPreview =
 previewParams.get('webview') ==='1'||
 previewParams.get('preview') ==='webview'||
 previewParams.get('app') ==='1';
 setIsWebViewEnv(
 isAndroidWebView || isIosWebView || isEmbeddedAppWebView || forceWebViewPreview
 );
 }, []);
 const tLanguage = useAtozasTranslate('Language');
 const tHome = useAtozasTranslate('Home');
 const tDashboard = useAtozasTranslate('Dashboard');
 const tMyRides = useAtozasTranslate('My Rides');
 const tDriverEarnings = useAtozasTranslate('Driver Earnings');
 const tAccount = useAtozasTranslate('Account');
 const tRides = useAtozasTranslate('Rides');
 const tProfile = useAtozasTranslate('Profile');
 const tFeedback = useAtozasTranslate('Feedback');
 const tTerms = useAtozasTranslate('Terms & Conditions');
 const tPrivacy = useAtozasTranslate('Privacy Policy');
 const tLogout = useAtozasTranslate('Logout');
 const tDriverDashboard = useAtozasTranslate('Driver Dashboard');
 const tBecomeDriver = useAtozasTranslate('Become a Driver');
 const tReadyToRide = useAtozasTranslate('Ready to ride');

 const isUserMobileBar = !isOnDriverDashboard;
 const isUserHomeRoute =
 location.pathname ==='/dashboard'|| location.pathname ==='/dashboard/';
 const showMobileHomeDriverCta = isUserMobileBar && isUserHomeRoute;

 // Helper function to get profile picture URL
 const getProfileImageUrl = (profilePicture) => {
 if (!profilePicture) return null;
 // If it's a full URL, return as is
 if (profilePicture.startsWith('http')) return profilePicture;
 // Otherwise, construct backend URL using centralized API helper
 const baseUrl = getApiUrl();
 return`${baseUrl}/${profilePicture.replace(/\\/g,'/')}`;
 };

 const handleNotificationClick = () => {
 if (onNotificationClick) {
 onNotificationClick();
 }
 // Don't toggle panel here - let parent component handle navigation
 };

 /* Mobile/tablet (browser):`fixed`—`sticky`breaks with overflow-x on ancestors.
 WebView:`fixed`at all breakpoints (sticky is unreliable in embedded WKWebView/Android WebView).
 Desktop browser (lg+):`sticky`in flow. */
 /* Driver dashboard mobile row is compact (no “Become driver” strip); keep spacer tight to avoid a gap under the bar. */
 const spacerShort ='h-[calc(env(safe-area-inset-top,0px)+3.5rem)]';
 const spacerTall ='h-[calc(env(safe-area-inset-top,0px)+10.75rem)]';
 const spacerWebLg ='lg:h-[calc(env(safe-area-inset-top,0px)+7.5rem)]';
 const spacerWebLgDriver ='lg:h-[calc(env(safe-area-inset-top,0px)+6rem)]';

 const navPositionClass = isWebViewEnv
 ?'fixed inset-x-0 top-0':'fixed inset-x-0 top-0 lg:sticky lg:top-0 lg:inset-x-auto';

 const spacerClass = isWebViewEnv
 ?`shrink-0 ${!isOnDriverDashboard ?`${spacerTall} ${spacerWebLg}`:`${spacerShort} ${spacerWebLgDriver}`}`
 :`shrink-0 lg:hidden ${
 !isOnDriverDashboard
 ? showMobileHomeDriverCta
 ? spacerTall
 : spacerShort
 : spacerShort
 }`;

 return (
 <>
 <nav
 className={`navbar z-50 pt-safe lg:bg-transparent lg:px-4 lg:pt-4 lg:shadow-none xl:px-6 lg: ${navPositionClass} ${
 isUserMobileBar
 ?'bg-[#f4f7fb] px-3 shadow-none'
 :'bg-[#f4f7fb] px-0 shadow-sm'}`}
 >
 <div className="mx-auto w-full max-w-screen-3xl px-0 lg:px-0 4xl:max-w-screen-4xl">
 <div className="lg:overflow-visible lg:rounded-[36px]">
 <div
 className={`relative flex items-center justify-between lg:min-h-[80px] lg:gap-4 lg:rounded-[36px] lg:border lg:border-[#cbded8] lg:bg-[#f7fbf9] lg:px-5 lg:py-3 lg:shadow-[0_20px_50px_-34px_rgba(15,23,42,0.42)] lg:ring-0 xl:min-h-[86px] xl:px-6 ${
 isUserMobileBar
 ?'min-h-[60px] gap-2 rounded-[22px] border-0 bg-white px-3 py-2.5 shadow-[0_10px_28px_-22px_rgba(15,23,42,0.14)] ring-1 ring-slate-100'
 :`gap-2 border-b border-slate-200/80 bg-white px-3 sm:gap-3 sm:px-5 ${isOnDriverDashboard ?'min-h-0 py-1.5':'min-h-[56px] py-2'}`}`}
 >
 {/* Menu Icon - Mobile Only */}
 {onMenuClick && (
 <button
 onClick={onMenuClick}
 className={`touch-target inline-flex h-10 w-10 shrink-0 items-center justify-center bg-slate-100 text-slate-800 ring-1 ring-slate-200/90 transition-colors hover:text-primary-600 lg:hidden ${
 isUserMobileBar ?'rounded-xl':'mr-1 rounded-full sm:mr-2'}`}
 aria-label="Open menu">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/>
 </svg>
 </button>
 )}

 {isOnDriverDashboard && (
 <div className="min-w-0 flex-1 lg:hidden">
 <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 xs:text-[11px]">UttaApp Driver</p>
 <h1 className="truncate text-base font-bold leading-tight text-slate-950 xs:text-[17px]">Dashboard</h1>
 </div>
 )}

 {/* Logo and App Name */}
 <div className={`${isOnDriverDashboard ?'hidden lg:flex':'relative flex min-w-0 flex-1'} items-center gap-2 sm:gap-3 lg:flex-initial`}>
 <button
 onClick={onHome}
 className="flex min-w-0 items-center gap-2 transition-opacity hover:opacity-90 sm:gap-3">
 <div className={`${isOnDriverDashboard ?'flex' :'hidden'} h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-[#d9e8e3] bg-white p-1 shadow-[0_10px_24px_-18px_rgba(15,23,42,0.28)] lg:hidden`}>
 <img
 src="/icon-192.png"alt="UttaApp Logo"className="h-full w-full rounded-[14px] object-cover"/>
 </div>
 {/* Logo - Hidden on mobile */}
 <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#d5e7e1] bg-white p-1.5 shadow-[0_12px_30px_-18px_rgba(15,23,42,0.24)] lg:flex xl:h-14 xl:w-14">
 <img
 src="/icon-192.png"alt="UttaApp Logo"className="h-full w-full rounded-full object-cover"/>
 </div>
 {/* App Name - Visible on all screens */}
 <h1 className="flex min-w-0 flex-col items-start font-bold leading-tight text-gray-900 lg:text-slate-950 lg:">
 <span
 translate="no"className="notranslate block truncate text-lg font-bold tracking-[-0.04em] text-slate-950 lg:text-2xl">
 UttaApp
 </span>
 <span
 className={`text-[10px] font-bold uppercase tracking-[0.18em] lg:hidden ${
 isUserMobileBar ?'block text-emerald-600' :'hidden sm:block text-slate-400'}`}>
 {tReadyToRide}
 </span>
 <span className="hidden whitespace-nowrap text-xs font-normal opacity-70 lg:block lg:text-[11px] lg:uppercase lg:tracking-[0.24em] lg:text-slate-500 lg:">beta/test version</span>
 </h1>
 </button>
 </div>

 {/* Navigation Items - Hidden on mobile/tablet, shown on laptop+ */}
 <div className="hidden lg:flex items-center gap-1.5 rounded-full border border-[#dbeae5] bg-[#edf7f3] p-1.5 shadow-[0_14px_30px_-24px_rgba(15,23,42,0.16)] xl:gap-2">
 {isOnDriverDashboard ? (
 <>
 {/* Driver Dashboard Tabs */}
 <button
 onClick={onHome}
 className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#0f766e]">
 {tHome}
 </button>
 <button
 onClick={() => onDriverTabChange && onDriverTabChange('dashboard')}
 className={`transition-colors font-medium ${activeDriverTab ==='dashboard'?'rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_-20px_rgba(15,118,110,0.7)]':'rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#0f766e]'}`}
 >
 {tDashboard}
 </button>
 <button
 onClick={() => onDriverTabChange && onDriverTabChange('rides')}
 className={`transition-colors font-medium ${activeDriverTab ==='rides'?'rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_-20px_rgba(15,118,110,0.7)]':'rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#0f766e]'}`}
 >
 {tMyRides}
 </button>
 <button
 onClick={() => onDriverTabChange && onDriverTabChange('earnings')}
 className={`transition-colors font-medium ${activeDriverTab ==='earnings'?'rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_-20px_rgba(15,118,110,0.7)]':'rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#0f766e]'}`}
 >
 {tDriverEarnings}
 </button>
 <button
 onClick={() => onDriverTabChange && onDriverTabChange('account')}
 className={`transition-colors font-medium ${activeDriverTab ==='account'?'rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_-20px_rgba(15,118,110,0.7)]':'rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#0f766e]'}`}
 >
 {tAccount}
 </button>
 </>
 ) : (
 <>
 {/* User Dashboard Tabs */}
 <button
 onClick={onHome}
 className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#0f766e]">
 {tHome}
 </button>
 <button
 onClick={onRides || (() => { })}
 className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#0f766e]">
 {tRides}
 </button>
 <button
 onClick={onProfile}
 className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#0f766e]">
 {tProfile}
 </button>
 </>
 )}
 </div>

 {/* Driver Action Button - Hidden on mobile/tablet */}
 <div className="hidden lg:flex items-center gap-2 xl:gap-3">
 {/* Language Selector */}
 <div className="flex items-center gap-1.5 rounded-full border border-[#dbeae5] bg-[#edf7f3] p-1.5 shadow-[0_14px_30px_-24px_rgba(15,23,42,0.16)] xl:gap-2">
 <LanguageSelector
 aria-label={tLanguage}
 title={tLanguage}
 className="max-w-[155px] rounded-full border border-[#d7eae4] bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0f766e]"/>
 <button
 type="button"onClick={() => navigate('/subscription-pricing')}
 className="inline-flex items-center gap-2 rounded-full border border-[#d7eae4] bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:text-[#0f766e] focus:outline-none focus:ring-2 focus:ring-[#0f766e] focus:ring-offset-2">
 <svg className="h-5 w-5 text-[#0f766e]"fill="none"stroke="currentColor"viewBox="0 0 24 24"aria-hidden="true">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z"/>
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M12 21a9 9 0 100-18 9 9 0 000 18z"/>
 </svg>
 <span className="font-semibold">{tAccount}</span>
 </button>
 </div>

 {isDriver && !isOnDriverDashboard ? (
 <button
 onClick={onDriverDashboard}
 className="landing-primary-button px-6 py-2.5 text-sm">
 {tDriverDashboard}
 </button>
 ) : !isDriver ? (
 <button
 onClick={onBecomeDriver}
 className="landing-primary-button px-6 py-2.5 text-sm">
 {tBecomeDriver}
 </button>
 ) : null}




 {/* Notification Bell Icon - Only shown on driver dashboard */}
 {isOnDriverDashboard && (
 <div className="relative">
 <button
 onClick={handleNotificationClick}
 className="relative rounded-full border border-[#d7eae4] bg-white p-2 text-slate-600 shadow-sm transition-colors hover:text-[#0f766e] focus:outline-none focus:ring-2 focus:ring-[#0f766e] focus:ring-offset-2"aria-label="Notifications">
 <svg className="w-6 h-6"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
 </svg>
 {/* Notification Badge */}
 {hasNotifications && (
 <span className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 border-2 border-white transform translate-x-1 -translate-y-1">
 <span className="text-white text-xs font-bold">{unreadCount > 9 ?'9+': unreadCount}</span>
 </span>
 )}
 </button>
 </div>
 )}

 
 {/* User Profile Dropdown */}
 <div className="relative z-[60] group">
 <button className="flex items-center space-x-2 rounded-full border border-[#d7eae4] bg-white px-2.5 py-1.5 text-slate-700 shadow-sm transition-colors hover:text-[#0f766e]">
 <div className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)]">
 {getProfileImageUrl(user?.profilePicture) ? (
 <img
 src={getProfileImageUrl(user.profilePicture)}
 alt="Profile"className="w-full h-full object-cover rounded-full absolute inset-0"onError={(e) => {
 e.target.style.display ='none';
 const fallback = e.target.parentElement?.querySelector('.profile-fallback');
 if (fallback) {
 fallback.style.display ='flex';
 }
 }}
 />
 ) : null}
 <span
 className="profile-fallback text-white font-semibold text-sm"style={{ display: getProfileImageUrl(user?.profilePicture) ?'none':'flex'}}
 >
 {user?.username ? user.username.charAt(0).toUpperCase() :'U'}
 </span>
 </div>
 <span className="hidden sm:block text-sm font-medium">{user?.username ||'User'}</span>
 <svg className="w-4 h-4"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M19 9l-7 7-7-7"/>
 </svg>
 </button>

 {/* Dropdown Menu */} 
 <div className="invisible absolute right-0 z-[70] mt-2 w-64 rounded-[20px] border border-[#d7eae4] bg-white shadow-[0_28px_60px_-32px_rgba(15,23,42,0.24)] opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
 <div className="py-2">
 <div className="border-b border-slate-100 px-4 py-2">
 <p className="text-sm font-medium text-slate-900">{user?.username ||'User'}</p>
 <p className="truncate text-xs text-slate-500"title={user?.email ||''}>{user?.email ||''}</p>
 </div>
 <button
 onClick={onProfile}
 className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e]">
 {tProfile}
 </button>
 <div className="mt-1 border-t border-slate-100">
 <a
 href="/feedback"onClick={(e) => {
 e.preventDefault();
 if (onProfile) onProfile();
 // Navigate using window.location or navigate hook if available
 window.location.href ='/feedback';
 }}
 className="block px-4 py-2 text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e]">
 {tFeedback}
 </a>
 <a
 href="/terms-conditions"onClick={(e) => {
 e.preventDefault();
 if (onProfile) onProfile();
 window.location.href ='/terms-conditions';
 }}
 className="block px-4 py-2 text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e]">
 {tTerms}
 </a>
 <a
 href="/privacy-policy"onClick={(e) => {
 e.preventDefault();
 if (onProfile) onProfile();
 window.location.href ='/privacy-policy';
 }}
 className="block px-4 py-2 text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e]">
 {tPrivacy}
 </a>
 </div>
 <div className="border-t border-slate-100">
 <button
 onClick={() => setShowLogoutConfirm(true)}
 className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
 {tLogout}
 </button>
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Mobile actions - Right side */}
 <div className="ml-auto flex shrink-0 items-center gap-2 lg:hidden">
 {isUserMobileBar && (
 <>
 <LanguageSelector
 aria-label={tLanguage}
 title={tLanguage}
 variant="pill"
 showSelectedCode
 className="!h-10 !min-h-[40px] !border !border-slate-200 !border-solid !bg-white !px-2.5 !py-0 shadow-none"
 />
 <button
 type="button"
 onClick={handleNotificationClick}
 className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f1f5f4] text-slate-800 ring-1 ring-slate-200/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0f766e] focus:ring-offset-2"
 aria-label="Notifications"
 >
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
 </svg>
 {hasNotifications ? (
 <span className="absolute right-2 top-2 flex h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
 ) : null}
 </button>
 </>
 )}
 {isOnDriverDashboard && (
 <button
 onClick={handleNotificationClick}
 className="relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
 aria-label="Notifications"
 >
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
 </svg>
 {hasNotifications && (
 <span className="absolute top-0 right-0 flex h-5 w-5 translate-x-1 -translate-y-1 transform items-center justify-center rounded-full border-2 border-white bg-red-500">
 <span className="text-xs font-bold text-white">{unreadCount > 9 ? '9+' : unreadCount}</span>
 </span>
 )}
 </button>
 )}
 </div>
 </div>
 </div>
 </div>

 {/* Mobile home only: Become Driver / Driver Dashboard CTA (desktop unchanged in lg:flex above) */}
 {showMobileHomeDriverCta && (
 <div className="mt-2 px-0 lg:hidden">
 <div className="mx-auto max-w-screen-sm rounded-[24px] bg-white/95 p-2 shadow-[0_16px_34px_-26px_rgba(15,23,42,0.18)] ring-1 ring-black/5">
 {isDriver && !isOnDriverDashboard ? (
 <button
 onClick={onDriverDashboard}
 className="touch-target w-full rounded-[18px] bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_34px_-22px_rgba(15,118,110,0.64)] transition-all duration-200 sm:text-base">
 {tDriverDashboard}
 </button>
 ) : !isDriver ? (
 <button
 onClick={onBecomeDriver}
 className="touch-target w-full rounded-[18px] bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_34px_-22px_rgba(15,118,110,0.64)] transition-all duration-200 sm:text-base">
 {tBecomeDriver}
 </button>
 ) : null}
 </div>
 </div>
 )}

 {/* Logout Confirmation Modal */}
 <ConfirmationModal
 show={showLogoutConfirm}
 title="Logout"message="Are you sure you want to logout?"confirmText="Yes"cancelText="No"onConfirm={() => {
 setShowLogoutConfirm(false);
 onLogout();
 }}
 onCancel={() => setShowLogoutConfirm(false)}
 confirmButtonClass="bg-red-600 hover:bg-red-700"/>
 </nav>
 {/* Reserve scroll space under fixed navbar (mobile/tablet; all sizes in WebView). */}
 <div aria-hidden className={spacerClass} />
 </>
 );
};

export default Navbar;

